import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { buildApiUrl } from '../config/api';
import '../css/ImportExcel.css';

const ImportExcel = ({ onImport }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadStatus, setUploadStatus] = useState('');
    const [duplicateDetails, setDuplicateDetails] = useState([]);
    const [validationErrors, setValidationErrors] = useState([]);
    const [rejectedRows, setRejectedRows] = useState([]);
    const [showErrorDetails, setShowErrorDetails] = useState(false);
    const [latestImportDate, setLatestImportDate] = useState(null);
    const [importsList, setImportsList] = useState([]);
    const [selectedImportId, setSelectedImportId] = useState('');
    
    // Nuovi stati per la selezione del periodo
    const [selectedMonth, setSelectedMonth] = useState('');
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    const API_BASE_URL = buildApiUrl('import-excel');

    // Funzione per recuperare la data più recente dei dati importati
    const fetchLatestImportDate = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/latest-import-date`);
            if (response.data.status === 'OK') {
                setLatestImportDate(response.data.latestMonth);
            }
        } catch (error) {
            console.error('Errore nel recupero della data più recente:', error);
        }
    };

    // Funzione per recuperare la lista degli import
    const fetchImportsList = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/imports-list`);
            
            if (response.data.status === 'OK') {
                const imports = response.data.imports || [];
                setImportsList(imports);
            } else {
                console.error('Response status not OK:', response.data.status);
                setImportsList([]);
            }
        } catch (error) {
            console.error('Errore nel recupero della lista import:', error);
            setImportsList([]);
        }
    };

    // Carica la data più recente e la lista import al mount del componente
    useEffect(() => {
        fetchLatestImportDate();
        fetchImportsList();
    }, []);

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Verifica che sia un file Excel
            const validTypes = [
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'application/vnd.ms-excel'
            ];
            
            if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls)$/i)) {
                alert('Seleziona un file Excel valido (.xlsx o .xls)');
                return;
            }
            
            setSelectedFile(file);
            setUploadStatus('');
            setDuplicateDetails([]);
            setValidationErrors([]);
            setRejectedRows([]);
            setShowErrorDetails(false);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            setUploadStatus('Seleziona un file prima di caricare');
            return;
        }

        if (!selectedMonth) {
            setUploadStatus('Seleziona un mese prima di caricare');
            return;
        }

        setIsUploading(true);
        setUploadStatus('Caricamento in corso...');

        try {
            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('month', selectedMonth);
            formData.append('year', selectedYear);

            const response = await axios.post(`${API_BASE_URL}/import-excel`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                timeout: 60000 // 60 secondi di timeout
            });

            // Gestiamo la risposta del backend
            const result = response.data;
            
            if (result.success) {
                const message = result.message || result.Message || 'Import completato con successo';
                setUploadStatus(message);
                
                // Gestisci righe rifiutate se presenti
                if (result.rejectedRows || result.RejectedRows) {
                    setRejectedRows(result.rejectedRows || result.RejectedRows || []);
                }
            } else {
                // Gestione errori dettagliata - controlla entrambe le convenzioni di naming
                const duplicateRows = result.duplicateRows || result.DuplicateRows;
                const validationErrors = result.validationErrors || result.ValidationErrors;
                const rejectedRows = result.rejectedRows || result.RejectedRows;
                
                if (duplicateRows && Array.isArray(duplicateRows) && duplicateRows.length > 0) {
                    // Errori di duplicazione - mostra solo il messaggio principale
                    const message = result.message || result.Message || 'Righe duplicate trovate';
                    setUploadStatus(` ${message} (${duplicateRows.length} righe duplicate trovate)`);
                    
                    // Salva i dettagli per la visualizzazione dettagliata
                    setDuplicateDetails(duplicateRows);
                } else if (validationErrors && Array.isArray(validationErrors) && validationErrors.length > 0) {
                    // Errori di validazione
                    const message = result.message || result.Message || 'Errori di validazione';
                    setUploadStatus(` ${message} (${validationErrors.length} errori di validazione)`);
                    setValidationErrors(validationErrors);
                } else if (rejectedRows && Array.isArray(rejectedRows) && rejectedRows.length > 0) {
                    // Righe rifiutate
                    const message = result.message || result.Message || 'Righe rifiutate durante l\'import';
                    setUploadStatus(` ${message} (${rejectedRows.length} righe rifiutate)`);
                    setRejectedRows(rejectedRows);
                } else {
                    const message = result.message || result.Message || 'Errore durante l\'import';
                    setUploadStatus(` ${message}`);
                }
            }

            if (onImport) {
                onImport({ action: 'import-excel', data: result });
            }

            // Ricarica la data più recente e la lista import dopo un import riuscito
            if (result.success) {
                fetchLatestImportDate();
                fetchImportsList();
            }
        } catch (error) {
            console.error('Errore durante il caricamento:', error);
            setUploadStatus(`Errore durante il caricamento: ${error.message}`);
        } finally {
            setIsUploading(false);
        }
    };

    const handleDownloadTemplate = async () => {
        try {
            setUploadStatus('Generazione del template in corso...');
            
            // Chiama l'endpoint backend per generare il template dinamicamente
            const response = await fetch(buildApiUrl('import-excel/download-template'), {
                method: 'GET',
                headers: {
                    'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                }
            });

            if (!response.ok) {
                throw new Error(`Errore HTTP: ${response.status}`);
            }

            // Ottieni il blob dal response
            const blob = await response.blob();
            
            // Crea un URL temporaneo per il blob
            const url = window.URL.createObjectURL(blob);
            
            // Crea un link temporaneo per il download
            const link = document.createElement('a');
            link.href = url;
            
            // Estrai il nome del file dall'header Content-Disposition o usa un nome di default
            const contentDisposition = response.headers.get('Content-Disposition');
            let fileName = 'Template_Excel_import.xlsx';
            if (contentDisposition) {
                const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/i);
                if (fileNameMatch) {
                    fileName = fileNameMatch[1];
                }
            }
            link.download = fileName;
            
            // Aggiungi il link al DOM
            document.body.appendChild(link);
            
            // Simula il click per avviare il download
            link.click();
            
            // Rimuovi il link dal DOM e libera l'URL
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            setUploadStatus('Template generato e scaricato con successo!');
        } catch (error) {
            console.error('Errore durante il download del template:', error);
            setUploadStatus(`Errore durante il download: ${error.message}`);
        }
    };



    const handleDeleteSelectedImport = async () => {
        if (!selectedImportId) {
            setUploadStatus('Seleziona un import da eliminare');
            return;
        }

        try {
            setUploadStatus('Eliminazione import selezionato in corso...');
            
            const response = await axios.delete(`${API_BASE_URL}/import/${selectedImportId}`);
            
            if (response.status === 200) {
                const result = response.data;
                setUploadStatus(result.message || 'Import eliminato con successo!');
                
                // Reset della selezione
                setSelectedImportId('');
                
                // Dopo l'eliminazione, chiama onImport per aggiornare i dati
                if (onImport) {
                    onImport({ action: 'delete-import', data: result });
                }

                // Ricarica la data più recente e la lista import dopo l'eliminazione
                fetchLatestImportDate();
                fetchImportsList();
            }
        } catch (error) {
            console.error('Errore durante l\'eliminazione:', error);
            setUploadStatus(`Errore durante l'eliminazione: ${error.message}`);
        }
    };

    const handleImportSelectionChange = (event) => {
        setSelectedImportId(event.target.value);
    };

    const getTotalErrorCount = () => {
        return duplicateDetails.length + validationErrors.length + rejectedRows.length;
    };

    return (
        <div className="import-excel">
            <div className="file-input-section">
                <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileChange}
                    disabled={isUploading}
                />
                
                {selectedFile && (
                    <div className="file-info">
                        <p>File selezionato: {selectedFile.name}</p>
                        <p>Dimensione: {(selectedFile.size / 1024).toFixed(2)} KB</p>
                    </div>
                )}
            </div>

            {/* Selettori per mese e anno */}
            <div className="period-selection">
                <div className="period-selector">
                    <label htmlFor="month-select">Mese:</label>
                    <select
                        id="month-select"
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        disabled={isUploading}
                    >
                        <option value="">Seleziona mese...</option>
                        <option value="1">Gennaio</option>
                        <option value="2">Febbraio</option>
                        <option value="3">Marzo</option>
                        <option value="4">Aprile</option>
                        <option value="5">Maggio</option>
                        <option value="6">Giugno</option>
                        <option value="7">Luglio</option>
                        <option value="8">Agosto</option>
                        <option value="9">Settembre</option>
                        <option value="10">Ottobre</option>
                        <option value="11">Novembre</option>
                        <option value="12">Dicembre</option>
                    </select>
                </div>

                <div className="period-selector">
                    <label htmlFor="year-select">Anno:</label>
                    <select
                        id="year-select"
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                        disabled={isUploading}
                    >
                        {Array.from({ length: 10 }, (_, i) => {
                            const year = new Date().getFullYear() - 2 + i;
                            return (
                                <option key={year} value={year}>
                                    {year}
                                </option>
                            );
                        })}
                    </select>
                </div>
                
                <div className="period-note">
                    <span>* seleziona mese e anno per caricare il file</span>
                </div>
            </div>

            {/* Nota con l'ultimo mese importato */}
            {latestImportDate && (
                <div className="import-note">
                    <p>Note: l'ultimo mese importato è <strong>{latestImportDate}</strong></p>
                </div>
            )}

            <div className="button-section">
                <button 
                    onClick={handleUpload} 
                    disabled={!selectedFile || !selectedMonth || isUploading}
                    className="upload-btn"
                >
                    {isUploading ? 'Caricamento...' : 'Carica File'}
                </button>
                
                <button 
                    onClick={handleDownloadTemplate}
                    className="download-btn"
                >
                    Scarica Template
                </button>
            </div>

            {/* Sezione Eliminazione Import */}
            <div className="delete-section">
                {importsList.length === 0 ? (
                    <div className="no-imports-message">
                        <p>⚠️ Nessun import trovato nel database.</p>
                        <p>Carica prima un file Excel per vedere gli import disponibili.</p>
                    </div>
                ) : (
                    <div className="delete-controls">
                        <select 
                            value={selectedImportId} 
                            onChange={handleImportSelectionChange}
                            className="import-select"
                        >
                            <option value="">Seleziona import da eliminare...</option>
                            {importsList.map(importItem => (
                                <option key={importItem.id} value={importItem.id}>
                                    {importItem.displayText}
                                </option>
                            ))}
                        </select>
                        
                        
                        <button 
                            onClick={handleDeleteSelectedImport}
                            disabled={!selectedImportId}
                            className="delete-selected-btn"
                        >
                            Elimina Import Selezionato
                        </button>
                    </div>
                )}
                
            </div>

            {uploadStatus && (
                <div className={`status-message ${uploadStatus.includes('Errore') ? 'error' : 'success'}`}>
                    {uploadStatus}
                </div>
            )}
            
            {/* Dettagli errori */}
            {(duplicateDetails.length > 0 || validationErrors.length > 0 || rejectedRows.length > 0) && (
                <div className="error-details">
                    <div className="error-controls">
                        <button 
                            className="toggle-error-details"
                            onClick={() => setShowErrorDetails(!showErrorDetails)}
                        >
                            {showErrorDetails ? 'Nascondi' : 'Mostra'} dettagli errori
                            <span className="error-count">
                                ({getTotalErrorCount()} totali)
                            </span>
                        </button>
                    </div>
                    
                    {showErrorDetails && (
                        <div className="error-details-content">
                            {/* Righe duplicate */}
                            {duplicateDetails.length > 0 && (
                                <div className="duplicate-errors">
                                    <h4>
                                        Righe Duplicate ({duplicateDetails.length})
                                        {duplicateDetails.length > 5 && (
                                            <span className="scroll-indicator"></span>
                                        )}
                                    </h4>
                                    <div className="duplicate-list">
                                        {duplicateDetails.map((row, index) => (
                                            <div key={index} className="duplicate-item">
                                                <div className="duplicate-header">
                                                    <strong>#{index + 1}</strong> | 
                                                    <strong>Foglio:</strong> {row.worksheetName || row.WorksheetName || 'N/A'} | 
                                                    <strong>Riga Excel:</strong> {row.excelRowNumber || row.ExcelRowNumber || 'N/A'}
                                                </div>
                                                <div className="duplicate-data">
                                                    <span><strong>Data:</strong> {row.date || row.Date || 'N/A'}</span>
                                                    <span><strong>Company:</strong> {row.company || row.Company || 'N/A'}</span>
                                                    <span><strong>Row:</strong> {row.description || row.Description || 'N/A'}</span>
                                                    <span><strong>Card:</strong> {row.cardId || row.CardId || 'N/A'}</span>
                                                    <span><strong>Valore:</strong> {row.valDec || row.ValDec || 'N/A'}</span>
                                                </div>
                                            </div>
                                        ))}
                                        
                                    </div>
                                </div>
                            )}
                            
                            {/* Righe rifiutate */}
                            {rejectedRows.length > 0 && (
                                <div className="rejected-errors">
                                    <h4>
                                        Righe Rifiutate ({rejectedRows.length})
                                        {rejectedRows.length > 5 && (
                                            <span className="scroll-indicator"></span>
                                        )}
                                    </h4>
                                    <div className="rejected-list">
                                        {rejectedRows.map((row, index) => (
                                            <div key={index} className="rejected-item">
                                                <div className="rejected-header">
                                                    <strong>#{index + 1}</strong> | 
                                                    <strong>Foglio:</strong> {row.worksheetName || 'N/A'} | 
                                                    <strong>Riga Excel:</strong> {row.excelRowNumber || 'N/A'}
                                                </div>
                                                <div className="rejected-reason">
                                                    <strong>Motivo:</strong> {row.rejectionReason || 'N/A'}
                                                </div>
                                                <div className="rejected-data">
                                                    {row.company && <span><strong>Company:</strong> {row.company}</span>}
                                                    {row.description && <span><strong>Row:</strong> {row.description}</span>}
                                                    {row.cardId && <span><strong>Card:</strong> {row.cardId}</span>}
                                                    {row.valDec && <span><strong>Valore:</strong> {row.valDec}</span>}
                                                    {row.date && <span><strong>Data:</strong> {row.date}</span>}
                                                </div>
                                            </div>
                                        ))}
                                        
                                    </div>
                                </div>
                            )}
                            
                            {/* Errori di validazione */}
                            {validationErrors.length > 0 && (
                                <div className="validation-errors">
                                    <h4>
                                        Errori di Validazione ({validationErrors.length})
                                        {validationErrors.length > 5 && (
                                            <span className="scroll-indicator"></span>
                                        )}
                                    </h4>
                                    <div className="validation-list">
                                        {validationErrors.map((error, index) => (
                                            <div key={index} className="validation-item">
                                                <strong>#{index + 1}</strong> {error}
                                            </div>
                                        ))}
                                        
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ImportExcel;